import React from 'react'
import { useTranslation } from 'react-i18next'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import CustomDateInput from './CustomDateInput'
import { ar, enUS } from 'date-fns/locale'
import { CiAlarmOn } from 'react-icons/ci'

type Option = {
	label_ar: string
	label_en: string
	value: string | number
}

type SearchInputProps = {
	type?: 'text' | 'email' | 'number' | 'date' | 'select'
	value: any
	onChange: (val: any) => void
	placeholder?: string
	label?: string
	icon?: React.ReactNode
	options?: Option[]
	withTime?: boolean
}

const SearchInput = ({
	type = 'text',
	value,
	onChange,
	placeholder,
	label,
	icon,
	options = [],
	withTime = false
}: SearchInputProps) => {
	const { i18n } = useTranslation()
	const isRTL = i18n.language === 'ar'
	const handleDateChange = (date: Date | null) => {
		if (!date) {
			onChange(null);
			return;
		}
		const newDate = value ? new Date(value) : new Date();
		newDate.setFullYear(date.getFullYear(), date.getMonth(), date.getDate());
		onChange(newDate);
	};

	const handleTimeChange = (time: Date | null) => {
		if (!time) {
			onChange(null);
			return;
		}
		const newDate = value ? new Date(value) : new Date();
		newDate.setHours(time.getHours(), time.getMinutes());
		onChange(newDate);
	};

	const renderInput = () => {
		if (type === 'date') {
			return (
				<div className="flex rounded-lg bg-gray-300  flex-row ">
					<div className={`${withTime?' w-3/5':"w-full"}`}>
						<DatePicker
							selected={value}
							onChange={handleDateChange}
							placeholderText={placeholder}
							dateFormat="yyyy-MM-dd"
							locale={i18n.language === 'ar' ? ar : enUS}
							className="w-full"
							minDate={new Date()}
							calendarClassName={isRTL ? 'rtl' : ''}
							customInput={
								<CustomDateInput
									placeholder={placeholder }
									icon={icon}
									isRTL={isRTL}
								/>
							}
						/>
					</div>
					{withTime && (
					<div className=' w-2/5'>
						<DatePicker
							selected={value}
							onChange={handleTimeChange}
							placeholderText={"HH:mm"}
							dateFormat="HH:mm"
							showTimeSelect
							showTimeSelectOnly
							timeIntervals={60}
							locale={i18n.language === 'ar' ? ar : enUS}
							className="w-full"
							calendarClassName={isRTL ? 'rtl' : ''}
							customInput={
								<CustomDateInput
									placeholder={i18n.language === 'ar' ? 'اختر الوقت' : 'Select time'}
									icon={<CiAlarmOn />}
									isRTL={isRTL}
								/>
							}
						/>
					</div>
					)}
				</div>
			);
		}

		if (type === 'select') {
			return (
				<select
					value={value}
					onChange={(e) => onChange(e.target.value)}
					className={`w-full bg-gray-300 text-textSecond rounded-lg border border-gray-300 px-4 py-2 focus:outline-none ${icon ? (isRTL ? 'pr-10' : 'pl-10') : ''}`}
				>
					{options.map((option, idx) => (
						<option key={idx} value={option.value}>
							{i18n.language === 'ar' ? option.label_ar : option.label_en}
						</option>
					))}
				</select>
			)
		}

		return (
			<input
				type={type}
				value={value}
				onChange={(e) => onChange(e.target.value)}
				placeholder={placeholder}
				className={`w-full bg-gray-300 text-textSecond rounded-lg border border-gray-300 py-2 focus:outline-none ${icon ? (isRTL ? 'pr-8' : 'pl-8') : ''}`}
			/>
		)
	}

	return (
		<div className="flex text-start flex-1 flex-col gap-2" dir={isRTL ? 'rtl' : 'ltr'}>
			{label && <label className="text-sm font-medium text-gray-700">{label}</label>}
			<div className="relative w-full">
				{icon && type !== 'date' && (
					<div className={`absolute inset-y-0 flex items-center text-primary ${isRTL ? 'right-3' : 'left-3'}`}>
						{icon}
					</div>
				)}
				{renderInput()}
			</div>
		</div>
	)
}

export default SearchInput
